import { t } from './config'
import { escapeHTML, blocksToHTML } from './richText'
import { widgetHTML } from './widgets'

// Widgets are placed after their message, not by arrival time. An early one waits.
// onWidgets runs once a widget is in the page, which for an early one is later.
export default function createTranscript (element, { onWidgets = () => {}, renderFeedback } = {}) {
  const pendingWidgets = new Map()
  const previews = new Map()
  let pinned = false

  function clear () {
    element.innerHTML = ''
    pinned = false
    pendingWidgets.clear()
    previews.clear()
  }

  function hasUserMessage (messageId) {
    return Boolean(element.querySelector(`.vjs-vera-user-turn[data-message-id="${escapeHTML(messageId)}"]`))
  }

  function userMessage ({ message_id: messageId, text, attachments = [] }) {
    unpin()
    append(`
      <div class="vsd-vera-turn vsd-vera-turn--user vjs-vera-user-turn" data-message-id="${messageId}">
        <span class="vsd-vera-turn__label">${t('you')}</span>
        ${attachments.map(attachment => `
          <img class="vsd-vera-turn__attachment" src="${escapeHTML(attachment.url)}" alt="${escapeHTML(attachment.filename || '')}">
        `).join('')}
        ${text ? `<p class="vsd-vera-turn__bubble">${escapeHTML(text)}</p>` : ''}
      </div>
    `)
    flushPending(messageId)
  }

  function messageStart ({ message_id: messageId }) {
    if (node(messageId)) return

    previews.set(messageId, '')
    append(`
      <div class="vsd-vera-turn vsd-vera-turn--assistant" data-message-id="${messageId}">
        <span class="vsd-vera-turn__label">${t('assistant')}</span>
        <div class="vsd-vera-turn__body vjs-vera-turn-body"></div>
      </div>
    `)
    flushPending(messageId)
  }

  // Deltas render as plain text: partial markdown is not renderable. A delta that lands
  // after done must not overwrite it.
  function messageDelta ({ message_id: messageId, text }) {
    messageStart({ message_id: messageId })
    if (!previews.has(messageId)) return

    const preview = previews.get(messageId) + text
    previews.set(messageId, preview)
    body(messageId).innerHTML = `<p>${escapeHTML(preview).replace(/\n/g, '<br>')}</p>`
    scrollToEnd()
  }

  function messageDone ({ message_id: messageId, blocks }) {
    messageStart({ message_id: messageId })
    previews.delete(messageId)
    body(messageId).innerHTML = blocksToHTML(blocks)
    scrollToEnd()
  }

  function widget (data) {
    const html = widgetHTML({
      widgetId: data.widget_id,
      kind: data.kind,
      payload: data.payload || {}
    }, renderFeedback)
    if (!html) return

    const anchor = data.after_message_id
    const target = anchor ? node(anchor) : null

    if (anchor && !target) {
      pendingWidgets.set(anchor, (pendingWidgets.get(anchor) || []).concat(html))
      return
    }

    if (target) {
      insertAfterGroup(target, html)
    } else {
      append(html)
    }
    onWidgets()
  }

  // After the reply and its widgets: the rating covers all of it.
  function replyFeedback (messageId, html) {
    const target = node(messageId)
    if (!target || element.querySelector(`[data-reply-id="${escapeHTML(messageId)}"]`)) return

    insertAfterGroup(target, html)
  }

  function error ({ user_message: message, retriable }) {
    unpin()
    append(`
      <div class="vsd-vera-turn vsd-vera-turn--error">
        <p class="vsd-vera-turn__text">${escapeHTML(message)}</p>
        ${retriable ? `<button type="button" class="vsd-button__outlined vsd-button__small vjs-vera-retry">${t('retry')}</button>` : ''}
      </div>
    `)
  }

  function node (messageId) {
    return element.querySelector(`[data-message-id="${messageId}"]`)
  }

  function body (messageId) {
    return node(messageId).querySelector('.vjs-vera-turn-body')
  }

  function flushPending (messageId) {
    const waiting = pendingWidgets.get(messageId)
    if (!waiting) return

    pendingWidgets.delete(messageId)
    waiting.forEach(html => insertAfterGroup(node(messageId), html))
    onWidgets()
  }

  function insertAfterGroup (target, html) {
    const last = lastOfGroup(target)
    last.insertAdjacentHTML('afterend', html)
    reveal(last.nextElementSibling)
  }

  function append (html) {
    element.insertAdjacentHTML('beforeend', html)
    reveal(element.lastElementChild)
  }

  // Pin a results turn to the top until the shopper speaks again, so the streaming reply
  // cannot push the carousel out of view.
  function reveal (revealed) {
    if (!revealed?.querySelector('.vjs-vera-carousel')) return scrollToEnd()

    // Safari snaps a new track to whichever tile it likes: start it at the first.
    revealed.querySelectorAll('.vjs-vera-carousel-track').forEach(track => { track.scrollLeft = 0 })
    pinned = true
    element.scrollTop += revealed.getBoundingClientRect().top - element.getBoundingClientRect().top
  }

  function unpin () {
    pinned = false
  }

  function settleAtEnd () {
    unpin()
    scrollToEnd()
  }

  function scrollToEnd () {
    if (pinned) return
    element.scrollTop = element.scrollHeight
  }

  return {
    element,
    clear,
    hasUserMessage,
    userMessage,
    messageStart,
    messageDelta,
    messageDone,
    widget,
    replyFeedback,
    error,
    unpin,
    settleAtEnd,
    scrollToEnd
  }
}

function lastOfGroup (node) {
  return node.nextElementSibling?.dataset.widgetId ? lastOfGroup(node.nextElementSibling) : node
}
